<script lang="ts" setup>
import { ref, watch, onMounted, nextTick, reactive } from "vue";
import type { FormInstance } from "element-plus";
import { useRouter } from "vue-router";
import { useMainStore } from "../store/main";
import { usePGRESTAPIStore } from "../store/pgrest_api";
import PGRESTResult from "../classes/PGRESTResult";
import { ElMessage } from "element-plus";
import { Edit, Delete, Notebook } from "@element-plus/icons-vue";
import ShortDescDetails from "../components/ConfigShortDescDetails.vue";
import UserType from "../enums/UserType";
import { DateTime } from "luxon";
import { useGenericMethodsVariables } from "../composables/genericMethodsVariables";
const { showError } = useGenericMethodsVariables();

const router = useRouter();
const mainStore = useMainStore();
const pgrestapi = usePGRESTAPIStore();
const formRef = ref<FormInstance>();
const tabPosition = ref("left");
const DeleteDialogVisible = ref(false);
let detailsVisible = ref(false);
let deleteVisible = ref(false);
let selectedRow = ref(0);
let DeleteSelectedRow = ref(0);
let dataList = ref([]);
let isLoading = ref(false);
let lastPage = ref(0);
let dataCount = ref(0);

const emits = defineEmits(["save", "close", "fail-save"]);

onMounted(async () => {
  loadPage(1);
});

const deleteHandleClick = (row_id: number) => {
  DeleteSelectedRow.value = row_id;
  deleteVisible.value = true;
  DeleteArgies(row_id);
};

const handleClick = (row_id: number) => {
  selectedRow.value = row_id;
  detailsVisible.value = true;
};

const DeleteArgies = async (row_id: number) => {
  let result: PGRESTResult = new PGRESTResult();

  let id = JSON.stringify(row_id);

  result = await pgrestapi.delete_data("config_short_descriptions?id=eq." + id);

  if (result.error) {
    showError(result.error.message);

    DeleteDialogVisible.value = false;
    return;
  }

  onDelete();
};

const onSave = async () => {
  selectedRow.value = -1;
  ElMessage({
    showClose: true,
    message: "Short description updated successfully",
    type: "success",
    duration: 1000,
  });

  detailsVisible.value = false;
  loadPage(lastPage.value);
};

const onInsert = async () => {
  selectedRow.value = -1;
  ElMessage({
    showClose: true,
    message: "Short description saved Successfully",
    type: "success",
    duration: 1000,
  });

  detailsVisible.value = false;
  loadPage(lastPage.value);
};

const onDelete = async () => {
  ElMessage({
    showClose: true,
    message: "Short description Deleted Successfully",
    type: "success",
    duration: 1000,
  });

  DeleteDialogVisible.value = false;
  loadPage(lastPage.value);
};

const onFailSave = async () => {
  selectedRow.value = -1;
  ElMessage({
    showClose: true,
    message: "Short description failed to update",
    type: "error",
    duration: 2000,
  });

  detailsVisible.value = false;
};

const dynamicValidateForm = reactive<{
    ShortDescs: ShortDescItem[];
}>({
    ShortDescs: [],
});

interface ShortDescItem {
  short_desc: string;
}

const removeShortDesc = (item: ShortDescItem) => {
  const index = dynamicValidateForm.ShortDescs.indexOf(item);
  if (index !== -1) {
    dynamicValidateForm.ShortDescs.splice(index, 1);
  }
};

const addShortDesc = () => {
  dynamicValidateForm.ShortDescs.push({
    short_desc: "",
  });
};

const submitForm = (formEl: FormInstance | undefined) => {
  if (!formEl) return;
  formEl.validate((valid) => {
    if (valid) {
      console.log("submit!");
      save();
    } else {
      console.log("error submit!");
    }
  });
};

const save = async () => {
  let result: PGRESTResult = new PGRESTResult();

  result = await pgrestapi.post_data("config_short_descriptions", dynamicValidateForm.ShortDescs);

  if (result.error) {
    onFailSave();
    return;
  }
  dynamicValidateForm.ShortDescs = [];
  onInsert();
};

const loadPage = async (page: number) => {
  isLoading.value = true;

  lastPage.value = page;
  page = page - 1;

  let result: PGRESTResult = await pgrestapi.getPaginated(
    "config_short_descriptions?order=short_desc",
    page
  );

  if (result.error) {
    showError(result.error.message);

    return;
  }

  if (result.headers)
    dataCount.value = parseInt(result.headers["content-range"].split("/")[1]);

  dataList.value = result.data;

  isLoading.value = false;
};
</script>

<template>
  <div style="height: 100%" v-loading="isLoading">
    <el-row :gutter="10" style="margin: 20px">
      <el-col :span="24">
        <el-table :data="dataList" stripe border table-layout="fixed" size="small">
          <el-table-column fixed sortable prop="short_desc" :label="$t('PlatformSettingsView.short_descriptions')" type="text" />
          <el-table-column fixed="right" :label="$t('global.operations')">
            <template #default="scope">
              <el-button link type="primary" size="small" class="btn-detail" @click="handleClick(scope.row.id)">
                <el-icon>
                  <Edit />
                </el-icon>
              </el-button>
              <el-button v-if="mainStore.loggedUser.user_type === UserType.Administrator" link type="primary"
                size="small" class="btn-delete" @click="DeleteDialogVisible = true; DeleteSelectedRow = scope.row.id;">
                <el-icon>
                  <Delete />
                </el-icon>
              </el-button>
            </template>
          </el-table-column>
        </el-table>
        <div>
          <el-pagination size="small" layout=" prev, pager, next" :page-size="10" :total="dataCount"
            @current-change="loadPage" />
        </div>

        <el-dialog v-model="DeleteDialogVisible" title="Short Description Delete" width="50%">
          <span>{{ $t("PlatformSettingsView.short_desc_delete_message") }}</span>
          <template #footer #default="scope">
            <span class="dialog-footer">
              <el-button @click="DeleteDialogVisible = false">Cancel</el-button>
              <el-button type="primary" @click="deleteHandleClick(DeleteSelectedRow)">Delete
              </el-button>
            </span>
          </template>
        </el-dialog>

        <el-dialog v-model="detailsVisible" title="Short Descriptions Details" width="50%">
          <ShortDescDetails :shortdescid="selectedRow" @save="onSave" @close="detailsVisible = false; selectedRow = -1;"
            @fail-save="onFailSave" />
        </el-dialog>

        <el-row :gutter="10">
          <el-col :span="24">
            <el-form ref="formRef" :model="dynamicValidateForm" label-width="220px">
              <el-row :gutter="10">
                <el-col :span="24">
                  <el-form-item v-for="(ShortDesc, index) in dynamicValidateForm.ShortDescs"
                    :label="$t('PlatformSettingsView.short_desc') + ' ' + index" :prop="'short_desc.' + index + '.value'"
                    style="border-bottom: 0px; margin-top: 30px">
                    <el-col :span="11">
                            <el-input
                            v-model="ShortDesc.short_desc"
                            autocomplete="off"
                            :placeholder="$t('PlatformSettingsView.short_desc')"
                            style="border-bottom: 3px solid #e3e3e3; width: 100% !important;"
                            />
                    </el-col>
                    <el-col :span="2">
                      <el-button style="float: right" @click.prevent="removeShortDesc(ShortDesc)">Delete
                      </el-button>
                    </el-col>
                  </el-form-item>
                </el-col>
              </el-row>
              <el-row :gutter="10">
                <el-col :span="24">
                  <el-form-item style="border-bottom: 0px; float: right; margin-left: 0px; margin-top: 30px;">
                    <el-button v-if="dynamicValidateForm.ShortDescs.length != 0" type="primary"
                      @click="submitForm(formRef)">
                       {{ $t('generic.save') }}
                    </el-button>
                    <el-button @click="addShortDesc">{{
                        $t("PlatformSettingsView.new_short_desc")
                    }}</el-button>
                    <!-- <el-button @click="resetForm(formRef)">Reset</el-button> -->
                  </el-form-item>
                </el-col>
              </el-row>
            </el-form>
          </el-col>
        </el-row>
      </el-col>
    </el-row>
  </div>
</template>

<style scoped>
.el-row {
  margin-bottom: 20px;
}

.el-row:last-child {
  margin-bottom: 0;
}

.el-col {
  border-radius: 4px;
}

.grid-content {
  border-radius: 4px;
  min-height: 36px;
}

.info {
  margin: auto;
  width: 50%;
  padding: 10px;
  text-align: center;
}
</style>
